import { parseEventDateStart } from './dates.js';
export const DEPOSIT_EXTRA_PERCENT = 3;
function finiteAmount(value) {
    const amount = typeof value === 'number' ? value : Number(String(value ?? '').replace(/,/g, '').trim());
    return Number.isFinite(amount) && amount > 0 ? amount : 0;
}
/** Returns a stable local YYYY-MM-DD key for a date value. */
export function formatEventDateKey(value) {
    const date = parseEventDateStart(value);
    if (!date || Number.isNaN(date.getTime()))
        return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function parseJsonValue(raw) {
    if (typeof raw !== 'string')
        return raw;
    const text = raw.trim();
    if (!text)
        return [];
    try {
        return JSON.parse(text);
    }
    catch {
        return [];
    }
}
/** Parses the JSON deposit schedule while tolerating legacy/partial rows. */
export function parseDepositSchedule(raw) {
    const parsed = parseJsonValue(raw);
    const rows = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === 'object' && Array.isArray(parsed.installments)
            ? parsed.installments
            : [];
    return rows
        .map((row) => {
        const amount = finiteAmount(row?.amount ?? row?.Amount);
        if (!amount)
            return null;
        return {
            ...(String(row?.id || '').trim() ? { id: String(row.id).trim() } : {}),
            ...(['deposit', 'installment', 'final'].includes(row?.kind) ? { kind: row.kind } : {}),
            amount,
            dueDate: formatEventDateKey(row?.dueDate ?? row?.date ?? row?.DueDate),
            ...(['auto', 'manual'].includes(row?.dueDateMode) ? { dueDateMode: row.dueDateMode } : {}),
            ...(String(row?.label ?? '').trim() ? { label: String(row.label).trim() } : {}),
            ...(typeof (row?.extraEnabled ?? row?.vatEnabled) === 'boolean' ? { extraEnabled: row.extraEnabled ?? row.vatEnabled } : {}),
        };
    })
        .filter((row) => !!row);
}
export function serializeDepositSchedule(rows) {
    if (!Array.isArray(rows))
        return '';
    const normalized = rows
        .map((row) => {
        const amount = finiteAmount(row?.amount);
        if (!amount)
            return null;
        const dueDate = formatEventDateKey(row?.dueDate);
        const label = String(row?.label ?? '').trim();
        return {
            amount, dueDate, ...(label ? { label } : {}),
            ...(String(row?.id || '').trim() ? { id: String(row.id).trim() } : {}),
            ...(['deposit', 'installment', 'final'].includes(row?.kind) ? { kind: row.kind } : {}),
            ...(['auto', 'manual'].includes(row?.dueDateMode) ? { dueDateMode: row.dueDateMode } : {}),
            ...(typeof (row?.extraEnabled ?? row?.vatEnabled) === 'boolean' ? { extraEnabled: row.extraEnabled ?? row.vatEnabled } : {}),
        };
    })
        .filter((row) => !!row);
    return normalized.length ? JSON.stringify(normalized) : '';
}
export function sumDepositSchedule(rows) {
    return (Array.isArray(rows) ? rows : parseDepositSchedule(rows)).reduce((sum, row) => sum + finiteAmount(row?.amount), 0);
}
/**
 * Returns the amount that should reduce the quotation balance.
 *
 * A schedule describes future obligations, never proof of receiving money.
 * Legacy jobs continue to use their explicit Actual Received total.
 */
export function getDepositPaidTotal(actualReceived, schedule) {
    return finiteAmount(actualReceived);
}
export function getInstallmentLabel(row, index, lang = 'th') {
    const base = row.kind === 'final'
        ? (lang === 'en' ? 'Remaining balance' : 'ยอดคงเหลือ')
        : row.kind === 'installment'
            ? (lang === 'en' ? `Installment ${index + 1}` : `งวดชำระที่ ${index + 1}`)
            : (lang === 'en' ? `Deposit ${index + 1}` : `มัดจำรอบที่ ${index + 1}`);
    return row.label ? `${base} (${row.label})` : base;
}
/** Parses a per-day time map keyed by YYYY-MM-DD. */
export function parseEventDayTimes(raw) {
    const parsed = parseJsonValue(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
        return {};
    const result = {};
    Object.entries(parsed).forEach(([date, value]) => {
        const key = formatEventDateKey(date);
        const time = String(value ?? '').trim();
        if (key && time)
            result[key] = time;
    });
    return result;
}
export function serializeEventDayTimes(map) {
    if (!map || typeof map !== 'object' || Array.isArray(map))
        return '';
    const result = {};
    Object.entries(map).forEach(([date, value]) => {
        const key = formatEventDateKey(date);
        const time = String(value ?? '').trim();
        if (key && time)
            result[key] = time;
    });
    return Object.keys(result).length ? JSON.stringify(result) : '';
}
export function getWorkingTimeForEventDate(job, day) {
    const key = formatEventDateKey(day);
    const perDay = parseEventDayTimes(job?.['Event Day Times']);
    return String(perDay[key] || job?.['Working Time'] || '').trim();
}
//# sourceMappingURL=payment-schedule.js.map